<script setup>
  import {computed, inject, provide, ref} from "vue";

  import ComponentAlert from "@components/Alerts/Alert.vue";
  import ComponentSearchWrapper from "@components/Autocomplete/SearchWrapper.vue";
  import ComponentRowOriginFromMergeBadge from "@components/Badges/RowOriginFromMergeBadge.vue";
  import ComponentTextarea from "@components/Textarea/Textarea.vue";
  import {router, usePage} from "@inertiajs/vue3";
  import RecordComponentMinCSearch from "@pages/Records/Components/MinCSearch.vue";
  import SymptomContextDialog from "@pages/Records/Components/Sections/SymptomBar/Components/ContextDialog.vue";
  import SymptomTable from "@pages/Records/Components/Sections/SymptomBar/Components/Table.vue";
  import {MedButton, MedInfoButton} from "@pharma4u/medicheck-components";
  import {usePrivacyGuard} from "@utils/Helpers/PrivacyGuard.js";

  defineOptions({name: "RecordComponentSectionSymptomBar"});

  const props = defineProps({
    isEditable: {
      type: Boolean,
      required: true,
    },
    changeIsEditable: {
      type: Function,
      required: true,
    },
    recordData: {
      type: Object,
      required: true,
    },
    problems: {
      type: Object,
      required: true,
    },
  });

  const page = usePage();

  const getIsMassUpdateProcessing = inject("getIsMassUpdateProcessing");
  const isMergeRecordTransfer = inject("isMergeRecordTransfer");
  const {validatePrivacy} = usePrivacyGuard();

  const refSearch = ref(null);
  const refSymptomContextDialog = ref(null);

  const infoText = ref(props.recordData.info);
  const mode = ref(null);

  const isMassUpdateProcessing = computed(() => getIsMassUpdateProcessing("symptom"));

  const privacyErrors = ref({});
  const validateInfo = (plaintext) => {
    setTimeout(() => {
      privacyErrors.value = {
        info: validatePrivacy(plaintext ?? "", "Text"),
      };
    }, 200);
  };

  const setPatientInfo = () => {
    router.patch(
      route("records.update", {
        patient: page.props.patient.id,
        record: props.recordData.id,
      }),
      {
        info: infoText.value,
      },
      {
        preserveScroll: true,
        only: ["flash", "errors", "record"],
      },
    );
  };

  const addSymptom = (symptom) => {
    router.post(
      route("symptoms.store", {
        patient: page.props.patient.id,
        record: props.recordData.id,
      }),
      {
        abdata_key_miv: symptom.Key_MIV,
        symptom: symptom.Name,
      },
      {preserveScroll: true, only: ["flash", "errors", "symptoms"]},
    );
  };

  const openDialog = (dialogName, data) => {
    switch (dialogName) {
      case "refSymptomContextDialog":
        refSymptomContextDialog.value.open(data);
        break;
    }
  };

  provide("symptomOpenDialog", openDialog);

  const toggleMode = (newMode) => {
    mode.value = mode.value === newMode ? null : newMode;
  };
</script>

<template>
  <div class="flex items-center space-x-2 px-2 pb-4">
    <h5 class="text-2xl">Probleme / Symptome</h5>

    <template v-if="!isEditable">
      <MedButton
        variant="primary"
        class="ml-auto"
        :data-test="getTestId('bar-set-editable-button')"
        @click="changeIsEditable"
      >
        Bearbeiten
      </MedButton>
    </template>

    <template v-if="isEditable">
      <MedInfoButton
        size="lg"
        side="top"
        aria-label="Typische Probleme / Symptome"
        :data-test="getTestId('symptom-bar-info-button')"
      >
        <p class="mb-2 text-center font-semibold">Typische (häufige) Probleme / Symptome (z. B.)</p>
        <ul class="list-disc space-y-1 pl-4 text-sm">
          <li>Schmerzen (wie stark)?</li>
          <li>Magenbeschwerden, Sodbrennen?</li>
          <li>Trockener Mund?</li>
          <li>Abgeschlagenheit, Müdigkeit?</li>
          <li>Konzentrationsschwäche, kognitive Störungen, Vigilanz?</li>
          <li>Atemnot, Atemgeräusche?</li>
          <li>Schlafstörung?</li>
          <li>Schwindel, Gangunsicherheit, Stürze?</li>
          <li>Blutungen (Nasenbluten, Zahnfleischbluten, schwarzer Stuhl,...)?</li>
          <li>Obstipation, Diarrhoe, Inkontinenz?</li>
          <li>Appetitlosigkeit, Übelkeit?</li>
          <li>Hautausschläge, Juckreiz?</li>
          <li>Depression, Stimmungsänderung?</li>
          <li>Verwirrtheit, Halluzination?</li>
          <li>Angst, Unruhe?</li>
          <li>Ohrgeräusche?</li>
          <li>Tremor, Zittern?</li>
        </ul>
      </MedInfoButton>

      <template v-if="(isEditable && recordData.symptoms?.length >= 2) || mode === 'multi-select'">
        <MedButton
          class="ml-auto"
          :data-test="getTestId('toggle-multi-select-button')"
          @click="toggleMode('multi-select')"
        >
          <template v-if="mode === 'multi-select'">Abbrechen</template>
          <template v-else>Mehrfachauswahl</template>
        </MedButton>
      </template>
    </template>
  </div>

  <!-- Alert for not loadable symptoms -->
  <ComponentAlert
    v-if="problems.unloadableSymptoms?.length > 0 || problems.unloadableSymptomsSolved?.length > 0"
    type="warning"
    :test-id="getTestId('bar')"
  >
    <h4 class="font-bold">
      In diesem Bereich befinden sich Symptome, die in der aktualisierten Datenbank ersetzt wurden.
    </h4>

    <p class="mt-2">
      Bitte tauschen Sie folgende Symptome durch geeignete Neue aus, damit sie im Check erkannt und berücksichtigt
      werden:
    </p>

    <ul class="mt-2 ml-8 list-disc" :data-test="getTestId('bar-unloadable')">
      <template v-if="problems.unloadableSymptoms?.length > 0">
        <li v-for="(item, index) in problems.unloadableSymptoms" :key="'unsolved-' + index">
          <em>{{ item.symptom }}</em>
        </li>
      </template>

      <template v-if="problems.unloadableSymptomsSolved?.length > 0">
        <!-- prettier-ignore -->
        <li v-for="(item, index) in problems.unloadableSymptomsSolved" :key="'solved-' + index">
          <em>{{ item.old.symptom }}</em>
          (kann ggf. ersetzt werden durch <em>{{ item.new.Name }}</em>)
        </li>
      </template>
    </ul>
  </ComponentAlert>

  <!-- Alert for contradictory warnings -->
  <ComponentAlert
    v-if="problems.contradictions && problems.contradictions.length > 0"
    type="warning"
    :test-id="getTestId('bar-contradictory-errors')"
  >
    <h4 class="font-bold">Es wurden widersprüchliche Angaben gemacht:</h4>

    <ul class="ml-8 list-disc">
      <li v-for="(contradiction, index) in problems.contradictions" :key="index" v-text="contradiction" />
    </ul>
  </ComponentAlert>

  <!-- Table -->
  <SymptomTable :record-data="recordData" :is-editable="isEditable" :test-id="getTestId('bar')" :mode="mode" />

  <!-- MinC Search -->
  <ComponentSearchWrapper
    v-if="isEditable"
    :test-id="getTestId('bar')"
    :set-focus="() => $refs.refSearch.focus()"
    :disabled="isMassUpdateProcessing"
    class="mt-4"
  >
    <RecordComponentMinCSearch
      ref="refSearch"
      label="Problem / Symptom"
      placeholder="Suchen, z. B. Übelkeit"
      class="ml-1 w-96"
      filter="symptoms"
      :reset-on-select="true"
      :filter-list="true"
      :disabled="isMassUpdateProcessing"
      :test-id="getTestId('bar')"
      @input="addSymptom"
    />
  </ComponentSearchWrapper>

  <!-- Textarea -->
  <div class="mt-5 grid w-full grid-cols-3 pb-6 lg:grid-cols-9 lg:grid-rows-1">
    <div class="col-span-3 lg:col-span-8">
      <div>
        <ComponentRowOriginFromMergeBadge v-if="isMergeRecordTransfer">
          Herkunft: Angaben im Textfeld zusammengefasst, ggf. Inhalt prüfen
        </ComponentRowOriginFromMergeBadge>

        <ComponentTextarea
          ref="patientinfo"
          v-model="infoText"
          label="Subjektive Eindrücke, weitere objektive Daten, Wünsche und Therapieziele"
          :encrypted="true"
          :disabled="!isEditable"
          :privacy-guard="privacyErrors.info"
          :test-id="getTestId('bar-info')"
          @decrypted="validateInfo($event)"
          @blur="setPatientInfo"
        />
      </div>
    </div>
  </div>

  <!-- Dialogs -->
  <SymptomContextDialog ref="refSymptomContextDialog" />
</template>
